import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { getTotalRevenue } from '../components/revenuehistory/utils';
import { normalizeDate, startOfPeriod, addPeriod } from './dateUtils';
import { DEFAULT_COST_TARGET } from './costTargets';

// Re-exported for callers that still import from this module.
export const BUDGET_TARGET_PERCENTAGE = DEFAULT_COST_TARGET;
export const BUDGET_HISTORY_WEEKS = 4;

export function isCurrentWeek(startDate: Date, now: Date = new Date()): boolean {
  const todayWeekStart = startOfPeriod(normalizeDate(now), 'week');
  const inputWeekStart = normalizeDate(startDate);
  return inputWeekStart.getTime() === todayWeekStart.getTime();
}

export function calculatePastWeekDifference(
  totalSpent: number,
  totalRevenue: number,
  targetPercent: number = BUDGET_TARGET_PERCENTAGE,
): { difference: number; allowed: number } {
  const allowed = (targetPercent / 100) * totalRevenue;
  return { difference: totalSpent - allowed, allowed };
}

// Today's revenue is also estimated because the data provider only publishes
// the previous day's revenue, so "today" never has complete data.
export function calculateCurrentWeekBudget(
  revenueRows: RevenueHistory[],
  costsSoFar: number,
  showExVatRevenue: boolean,
  now: Date = new Date(),
  targetPercent: number = BUDGET_TARGET_PERCENTAGE,
): {
  remaining: number;
  allowed: number;
  estimatedWeekRevenue: number;
  settledRevenue: number;
  estimatedRemaining: number;
} {
  const today = normalizeDate(now);
  const weekStart = startOfPeriod(today, 'week');
  const weekEnd = addPeriod(weekStart, 'week', 1);

  let settledRevenue = 0;
  for (const r of revenueRows) {
    const d = normalizeDate(new Date(r.date));
    if (d.getTime() >= weekStart.getTime() && d.getTime() < today.getTime()) {
      settledRevenue += getTotalRevenue(r, showExVatRevenue, true);
    }
  }

  let estimatedRemaining = 0;
  for (
    let d = new Date(today);
    d.getTime() < weekEnd.getTime();
    d.setDate(d.getDate() + 1)
  ) {
    estimatedRemaining += averageRevenueForDayOfWeek(
      revenueRows,
      d,
      showExVatRevenue,
      BUDGET_HISTORY_WEEKS,
    );
  }

  const estimatedWeekRevenue = settledRevenue + estimatedRemaining;
  const allowed = (targetPercent / 100) * estimatedWeekRevenue;
  const remaining = allowed - costsSoFar;

  return { remaining, allowed, estimatedWeekRevenue, settledRevenue, estimatedRemaining };
}

function averageRevenueForDayOfWeek(
  revenueRows: RevenueHistory[],
  targetDate: Date,
  showExVat: boolean,
  weeks: number,
): number {
  const target = normalizeDate(targetDate);
  let sum = 0;
  for (let i = 1; i <= weeks; i++) {
    const past = new Date(target);
    past.setDate(past.getDate() - 7 * i);
    const pastNorm = normalizeDate(past);

    let dayTotal = 0;
    for (const r of revenueRows) {
      const d = normalizeDate(new Date(r.date));
      if (d.getTime() === pastNorm.getTime()) {
        dayTotal += getTotalRevenue(r, showExVat, true);
      }
    }
    sum += dayTotal;
  }
  return sum / weeks;
}
